// frontend/src/pages/ClientRequests.tsx
// Версия: 3.0.0 (2026-02-24)
// ИЗМЕНЕНИЯ v3.0.0:
// - h1 → Typography + кнопка "Новая заявка"
// - localeText для DataGrid (русификация)
// - Клик по строке → Dialog с деталями заявки
// - Статус Chip вместо текста (централизованный statusLabels)
// - flex-колонки для устранения горизонтального скролла
// - overflow: 'hidden' на корневом Box
import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Chip,
  Grid,
  IconButton,
  Tooltip,
} from '@mui/material';
import { DataGrid, GridColDef, GridPaginationModel } from '@mui/x-data-grid';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import ReplayIcon from '@mui/icons-material/Replay';
import { useNavigate } from 'react-router-dom';
import { BackendService } from '../services/BackendService';
import { getRequestStatusLabel, getRequestStatusColor } from '../utils/statusLabels';
import { formatTimeWithDate, formatIntervalForGrid } from '../utils/formatTestInterval';
import { buildRequestResendState } from '../utils/requestResendPrefill';

interface Request {
  id: number;
  ssas_number?: string;
  vessel_name: string;
  mmsi: string;
  imo_number?: string;
  planned_test_date?: string;
  test_date?: string;
  start_time?: string;
  end_time?: string;
  status?: string;
  source?: string;
  contact_person?: string;
  contact_email?: string;
  contact_phone?: string;
  owner_organization?: string;
  ship_owner?: string;
  additional_emails?: string[] | string | null;
  additionalEmails?: string[] | string | null;
  notes?: string;
  rejection_reason?: string;
  created_at?: string;
  updated_at?: string;
  [key: string]: unknown;
}

function formatSource(source?: string): string {
  if (!source) return '\u2014';
  if (source === 'WEB_FORM') return '\u0412\u0435\u0431-\u0444\u043e\u0440\u043c\u0430';
  return source.replace(/,/g, ' + ');
}

function normalizeList<T extends object>(input: unknown): T[] {
  if (Array.isArray(input)) return input as T[];
  if (input && typeof input === 'object') {
    const obj = input as { success?: boolean; data?: unknown };
    if (obj.success && Array.isArray(obj.data)) {
      return obj.data as T[];
    }
    const maybeData = (input as { data?: unknown }).data;
    if (Array.isArray(maybeData)) return maybeData as T[];
  }
  return [];
}

function formatDateTime(dateStr?: string): string {
  if (!dateStr) return '—';
  try {
    return (
      new Date(dateStr).toLocaleString('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        timeZone: 'UTC',
      }) + ' UTC'
    );
  } catch {
    return dateStr;
  }
}

function formatAdditionalEmails(value: unknown): string {
  if (Array.isArray(value)) {
    const emails = value.map((item) => String(item).trim()).filter(Boolean);
    return emails.length > 0 ? emails.join(', ') : '—';
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return '—';

    if (trimmed.startsWith('[')) {
      try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) return formatAdditionalEmails(parsed);
      } catch {
        // Fall through to rendering the original string.
      }
    }

    return trimmed;
  }

  return '—';
}

const ClientRequests: React.FC = () => {
  const navigate = useNavigate();
  const [requests, setRequests] = useState<Request[]>([]);
  const [loading, setLoading] = useState(true);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 10,
  });

  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<Request | null>(null);

  useEffect(() => {
    const fetchRequests = async () => {
      try {
        setLoading(true);
        const response = await BackendService.getMyRequests();
        const data = normalizeList<Request>(response);
        setRequests(data);
      } catch (error) {
        console.error('Ошибка загрузки заявок:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchRequests();
  }, []);

  const handleRowClick = (params: any) => {
    if (params?.row) {
      setSelectedRequest(params.row as Request);
      setDetailsOpen(true);
    }
  };
  const handleCloseDetails = () => {
    setDetailsOpen(false);
    setSelectedRequest(null);
  };

  const handleSendAgain = (request: Request) => {
    navigate('/client/request/new', {
      state: buildRequestResendState(request),
    });
  };

  const columns: GridColDef[] = [
    { field: 'id', headerName: 'ID', width: 60, flex: 0 },
    {
      field: 'created_at',
      headerName: 'Дата создания',
      width: 160,
      renderCell: (params: any) => formatDateTime(params.value),
    },
    { field: 'ssas_number', headerName: 'Терминал SSAS', width: 130, flex: 0 },
    { field: 'vessel_name', headerName: 'Судно', minWidth: 140, flex: 1 },
    { field: 'imo_number', headerName: 'IMO', width: 90, flex: 0 },
    { field: 'mmsi', headerName: 'MMSI', width: 100, flex: 0 },
    {
      field: 'planned_test_date',
      headerName: 'Интервал теста (UTC)',
      width: 200,
      flex: 0,
      renderCell: (params: any) => {
        const row = params.row;
        if (!row) return '';
        const interval = formatIntervalForGrid(
          row.start_time,
          row.end_time,
          row.planned_test_date || row.test_date
        );
        return (
          <Box sx={{ lineHeight: 1.3, py: 0.5 }}>
            <div>{interval.line1}</div>
            {interval.line2 && <div style={{ fontSize: '0.85em' }}>{interval.line2}</div>}
          </Box>
        );
      },
    },
    {
      field: 'status',
      headerName: 'Статус',
      width: 140,
      flex: 0,
      renderCell: (params: any) => {
        const status = String(params?.row?.status || '');
        return (
          <Chip
            label={getRequestStatusLabel(status)}
            color={getRequestStatusColor(status)}
            size="small"
          />
        );
      },
    },
    {
      field: 'source',
      headerName: 'Источник',
      width: 130,
      flex: 0,
      renderCell: (params: any) => {
        const src = formatSource(params?.row?.source);
        return <Chip label={src} size="small" variant="outlined" />;
      },
    },
    { field: 'contact_phone', headerName: 'Телефон', minWidth: 140, flex: 1 },
    {
      field: 'send_again',
      headerName: '',
      width: 56,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params: any) => (
        <Tooltip title="Отправить снова">
          <IconButton
            size="small"
            aria-label="Отправить снова"
            onClick={(event) => {
              event.stopPropagation();
              handleSendAgain(params.row as Request);
            }}
          >
            <ReplayIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  const localeText = {
    noRowsLabel: 'Нет заявок',
    MuiTablePagination: {
      labelRowsPerPage: 'Строк на странице',
    },
  };

  return (
    <Box sx={{ p: 3, overflow: 'hidden' }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h4" fontWeight="bold">
          Мои заявки
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => navigate('/client/request/new')}
        >
          Новая заявка
        </Button>
      </Box>

      <DataGrid
        rows={requests}
        columns={columns}
        loading={loading}
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        pageSizeOptions={[5, 10, 25]}
        initialState={{
          sorting: { sortModel: [{ field: 'id', sort: 'desc' }] },
        }}
        checkboxSelection={false}
        disableRowSelectionOnClick
        onRowClick={handleRowClick}
        sx={{ cursor: 'pointer' }}
        getRowHeight={() => 'auto' as const}
        autoHeight
        localeText={localeText}
      />

      {/* ========= ДИАЛОГ ДЕТАЛЕЙ ЗАЯВКИ ========= */}
      <Dialog open={detailsOpen} onClose={handleCloseDetails} maxWidth="md" fullWidth>
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            bgcolor: '#1976d2',
            color: 'white',
          }}
        >
          <Typography variant="h6">Заявка #{selectedRequest?.id}</Typography>
          <IconButton onClick={handleCloseDetails} sx={{ color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {selectedRequest && (
            <Grid container spacing={2} sx={{ mt: 0 }}>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Судно:
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {selectedRequest.vessel_name || '—'}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Терминал ССТО:
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    fontFamily: 'monospace',
                    bgcolor: '#e3f2fd',
                    px: 1,
                    borderRadius: 1,
                    display: 'inline-block',
                  }}
                >
                  {selectedRequest.ssas_number || '—'}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  IMO:
                </Typography>
                <Typography variant="body1">{selectedRequest.imo_number || '—'}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  MMSI:
                </Typography>
                <Typography variant="body1">{selectedRequest.mmsi || '—'}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Интервал теста (UTC):
                </Typography>
                {selectedRequest.start_time || selectedRequest.end_time ? (
                  <Box>
                    <Typography variant="body1">
                      Начало:{' '}
                      {formatTimeWithDate(
                        selectedRequest.start_time,
                        selectedRequest.planned_test_date || selectedRequest.test_date
                      )}
                    </Typography>
                    <Typography variant="body1">
                      Окончание:{' '}
                      {formatTimeWithDate(
                        selectedRequest.end_time,
                        selectedRequest.planned_test_date || selectedRequest.test_date
                      )}
                    </Typography>
                  </Box>
                ) : (
                  <Typography variant="body1">
                    {selectedRequest.planned_test_date || selectedRequest.test_date || '—'}
                  </Typography>
                )}
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Статус:
                </Typography>
                <Chip
                  label={getRequestStatusLabel(String(selectedRequest.status || ''))}
                  color={getRequestStatusColor(String(selectedRequest.status || ''))}
                  size="small"
                  sx={{ fontWeight: 600 }}
                />
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
              </Grid>

              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Email:
                </Typography>
                <Typography variant="body1">{selectedRequest.contact_email || '—'}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Дополнительная адресация:
                </Typography>
                <Typography variant="body1">
                  {formatAdditionalEmails(
                    selectedRequest.additional_emails ?? selectedRequest.additionalEmails
                  )}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Телефон:
                </Typography>
                <Typography variant="body1">{selectedRequest.contact_phone || '—'}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Организация:
                </Typography>
                <Typography variant="body1">{selectedRequest.owner_organization || '—'}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Дата создания:
                </Typography>
                <Typography variant="body1">
                  {formatDateTime(selectedRequest.created_at)}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Обновлено:
                </Typography>
                <Typography variant="body1">
                  {formatDateTime(selectedRequest.updated_at)}
                </Typography>
              </Grid>
              {['rejected', 'failed'].includes(selectedRequest.status || '') && (
                <Grid item xs={12}>
                  <Typography variant="body2" color="error.main" fontWeight="bold">
                    Причина отклонения:
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{ bgcolor: '#ffebee', p: 1, borderRadius: 1, mt: 0.5 }}
                  >
                    {selectedRequest.rejection_reason ||
                      selectedRequest.notes ||
                      'Причина не указана'}
                  </Typography>
                </Grid>
              )}
              {selectedRequest.notes &&
                !['rejected', 'failed'].includes(selectedRequest.status || '') && (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">
                      Примечания:
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{ bgcolor: '#f5f5f5', p: 1, borderRadius: 1, mt: 0.5 }}
                    >
                      {selectedRequest.notes}
                    </Typography>
                  </Grid>
                )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          {selectedRequest && (
            <Button
              onClick={() => handleSendAgain(selectedRequest)}
              variant="outlined"
              startIcon={<ReplayIcon />}
            >
              Отправить снова
            </Button>
          )}
          <Button onClick={handleCloseDetails} variant="contained">
            Закрыть
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ClientRequests;
